/** Parse an org warehouses CSV into {code, name, abbrev} rows.
 *
 * Header row optional: recognised column names map to fields (code / Codigo_Bodega /
 * warehouse id, name / city / nombre, abbrev / abreviatura). Without a recognised
 * header the columns are read in the tab's order: name, abbrev, code.
 * Delimiter is `,` or `;` (Excel in es/pt locales), whichever the first line has more of.
 */
export interface WarehouseRow { code: string; name: string; abbrev: string }

const HEADERS: Record<string, keyof WarehouseRow> = {
  code: 'code', id: 'code', warehouseid: 'code', codigobodega: 'code', bodega: 'code', codigo: 'code', location: 'code',
  name: 'name', city: 'name', nombre: 'name', ciudad: 'name', nome: 'name', cidade: 'name',
  abbrev: 'abbrev', abbreviation: 'abbrev', abreviatura: 'abbrev', abrev: 'abbrev',
};

const headerKey = (s: string) =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z]/g, '');

function splitLine(line: string, delim: string): string[] {
  const out: string[] = [];
  let cur = '';
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (quoted) {
      if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (c === '"') quoted = false;
      else cur += c;
    } else if (c === '"') quoted = true;
    else if (c === delim) { out.push(cur); cur = ''; }
    else cur += c;
  }
  out.push(cur);
  return out.map(s => s.trim());
}

export function parseWarehouseCsv(text: string): WarehouseRow[] {
  // ponytail: quoted fields can't span lines; warehouse lists don't need that.
  const lines = text.replace(/^﻿/, '').split(/\r?\n/).filter(l => l.trim());
  if (!lines.length) return [];
  const delim = (lines[0].split(';').length > lines[0].split(',').length) ? ';' : ',';
  const first = splitLine(lines[0], delim).map(h => HEADERS[headerKey(h)]);
  const hasHeader = first.some(Boolean);
  const cols: (keyof WarehouseRow | undefined)[] = hasHeader ? first : ['name', 'abbrev', 'code'];
  return (hasHeader ? lines.slice(1) : lines).map(line => {
    const row: WarehouseRow = { code: '', name: '', abbrev: '' };
    splitLine(line, delim).forEach((v, i) => { const k = cols[i]; if (k && !row[k]) row[k] = v; });
    row.abbrev = row.abbrev.toUpperCase();
    return row;
  }).filter(r => r.code);
}

/** Upsert imported rows into the current list by code (case-insensitive); new codes append. */
export function mergeWarehouses(current: WarehouseRow[], imported: WarehouseRow[]): WarehouseRow[] {
  const out = current.map(w => ({ ...w }));
  for (const w of imported) {
    const i = out.findIndex(o => o.code.trim().toUpperCase() === w.code.toUpperCase());
    if (i >= 0) out[i] = { code: out[i].code, name: w.name || out[i].name, abbrev: w.abbrev || out[i].abbrev };
    else out.push(w);
  }
  return out;
}
